import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/Icon.tsx";
import { Empty, ErrorView, Loading, Page, Section, useAddParam, useFail, useToast } from "../components/ui.tsx";
import { api, isQueued } from "../lib/api.ts";
import { dateShort, daysUntil } from "../lib/format.ts";
import { TaskSheet } from "./Person.tsx";

const BUCKETS = [["overdue", "Overdue"], ["today", "Today"], ["week", "This week"], ["later", "Later"], ["someday", "No date"], ["done", "Done"]] as const;

export function Tasks() {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const [scope, setScope] = useState<"mine" | "created">("mine");
  const [showDone, setShowDone] = useState(false);
  const [adding, setAdding] = useState(false);
  useAddParam(() => setAdding(true));
  const q = useQuery({ queryKey: ["tasks", scope, showDone], queryFn: () => api.get<any[]>(`/tasks?scope=${scope}&status=${showDone ? "all" : "open"}`) });
  const toggle = async (t: any) => {
    try {
      const r = await api.patch(`/tasks/${t.id}`, { status: t.status === "open" ? "done" : "open" }, t.version, { offline: `Tick off "${t.title}"` });
      toast(isQueued(r) ? "Saved on this phone. It'll sync when you're back in range." : t.status === "open" ? "Done" : "Reopened");
      if (isQueued(r)) qc.setQueryData<any[]>(["tasks", scope, showDone], (old) => old?.map((x) => (x.id === t.id ? { ...x, status: t.status === "open" ? "done" : "open", bucket: t.status === "open" ? "done" : x.bucket } : x)).filter((x) => showDone || x.status === "open"));
      else { qc.invalidateQueries({ queryKey: ["tasks"] }); qc.invalidateQueries({ queryKey: ["today"] }); }
    } catch (e) { fail(e); }
  };
  return (
    <Page title="Tasks" actions={<button className="iconbtn" aria-label="New task" onClick={() => setAdding(true)}><Icon name="plus" /></button>}>
      <div className="chips">
        <button className={"chip" + (scope === "mine" ? " on" : "")} onClick={() => setScope("mine")}>Mine</button>
        <button className={"chip" + (scope === "created" ? " on" : "")} onClick={() => setScope("created")}>Set by me for others</button>
        <button className={"chip" + (showDone ? " on" : "")} onClick={() => setShowDone(!showDone)}>Show done</button>
      </div>
      {q.isLoading ? <Loading /> : q.error ? <ErrorView error={q.error} /> : q.data!.length === 0 ? <Empty>No tasks.</Empty> : BUCKETS.map(([b, title]) => {
        const items = q.data!.filter((t) => t.bucket === b);
        if (!items.length) return null;
        return (
          <Section key={b} title={title} count={items.length}>
            <div className="list">
              {items.map((t) => (
                <div key={t.id} className={"row" + (b === "overdue" ? " overdue" : "")}>
                  <button className="iconbtn" aria-label={t.status === "open" ? `Done: ${t.title}` : `Reopen: ${t.title}`} onClick={() => toggle(t)}
                    style={t.status !== "open" ? { background: "var(--blue-btn)", boxShadow: "none", color: "#fff" } : undefined}><Icon name="check" /></button>
                  <Link to={t.person_id ? `/people/${t.person_id}` : t.listing_id ? `/listings/${t.listing_id}` : "#"} className="main-col">
                    <div className="title" style={t.status !== "open" ? { textDecoration: "line-through", color: "var(--muted)" } : undefined}>{t.title}</div>
                    <div className="sub">{[scope === "created" ? `For ${t.assigned_name}` : t.created_by_name && t.created_by_name !== t.assigned_name ? `From ${t.created_by_name}` : null,
                      t.person_name, t.listing_address, t.priority !== "normal" ? t.priority : null].filter(Boolean).join(" · ")}</div>
                  </Link>
                  <span className="when">{t.due_on ? (b === "overdue" ? `${-daysUntil(t.due_on)}d late` : b === "today" ? "Today" : dateShort(t.due_on)) : ""}</span>
                </div>
              ))}
            </div>
          </Section>
        );
      })}
      {adding && <TaskSheet onClose={() => setAdding(false)} onDone={() => { qc.invalidateQueries({ queryKey: ["tasks"] }); qc.invalidateQueries({ queryKey: ["today"] }); }} />}
    </Page>
  );
}
